import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {useHover} from '@mantine/hooks';
import {css, StyleSheet} from 'aphrodite';
import React, {useRef, useState} from 'react';
import {Handle, HandleProps, HandleType, Position, useStore} from 'reactflow';

import * as Colors from 'ui-library/Colors';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import {styles as tooltipStyles} from 'ui-library/floating-ui/Tooltip';
import ClickableIcon, {ClickableIconStyle} from 'ui-library/icons/ClickableIcon';
import {isEscapeKey} from 'util/Keyboard';
import {AtMostOneOf, ElementKeyboardEvent, ElementMouseEvent, MakeFieldsRequired} from 'util/Types';

const HANDLE_DOT_WIDTH = 10;
const ICON_WIDTH = 12;
const ICON_PADDING = 2.5;
const ICON_BORDER_WIDTH = 1.5;

const TOTAL_ICON_WIDTH = ICON_WIDTH + ICON_PADDING * 2 + ICON_BORDER_WIDTH * 2;
const HANDLE_DOT_MARGIN = (TOTAL_ICON_WIDTH - HANDLE_DOT_WIDTH) / 2;
const SPACE_BETWEEN_NODE_AND_HANDLE = TOTAL_ICON_WIDTH / 2 + HANDLE_DOT_WIDTH;

/**
 * Magnitude of the horizontal offset used to float the handle beside the node — the same distance
 * {@link calculateTranslation} shifts the handle away from the node edge.
 */
export const ADD_NODE_HANDLE_OUTER_REACH = SPACE_BETWEEN_NODE_AND_HANDLE;

/**
 * Position handles slightly away from the node, horizontally, so the handles appear to "float" alongside it,
 * and to provide room for <AddNodeIcon>.
 */
function calculateTranslation(type: HandleType): string {
  // Space the handle away from the node, so it appears to 'float' beside it, and has room for the add icon
  const translateX = type === 'target' ? -SPACE_BETWEEN_NODE_AND_HANDLE : SPACE_BETWEEN_NODE_AND_HANDLE;
  // Adjust for the node's height, so the handle is vertically centered along the node's side.
  const translateY = -TOTAL_ICON_WIDTH / 2;
  return `translate(${translateX}px, ${translateY}px)`;
}

/**
 * A helper for optionally wrapping children with a controlled tooltip (by way of `Popover` and manually
 * applying tooltip styling) which is controlled by the parent `AddNodeHandle`.
 */
function ControlledAddNodeHandleTooltip({
  shouldWrapWithTooltip,
  isOpen,
  children,
  tooltipText,
  placement,
  type,
}: {
  shouldWrapWithTooltip: boolean;
  isOpen: boolean;
  children: JSX.Element;
  tooltipText?: string;
  placement?: FloatingPlacement;
  type: HandleType;
}): JSX.Element {
  if (!shouldWrapWithTooltip || !tooltipText) {
    return children;
  }

  const calculatedTooltipPlacement =
    placement ?? (type === 'target' ? FloatingPlacement.LEFT : FloatingPlacement.RIGHT);

  return (
    <Popover
      isOpen={isOpen}
      interaction={FloatingInteraction.CONTROLLED}
      renderContent={() => <div className={css(tooltipStyles.tooltipText)}>{tooltipText}</div>}
      placement={calculatedTooltipPlacement}
      returnFocusOnClose={false}
      dataTestAttrs={{
        component: 'AddNodeHandle',
        element: 'tooltip',
      }}
    >
      {children}
    </Popover>
  );
}

export type AddNodeHandleProps = MakeFieldsRequired<Omit<HandleProps, 'position'>, 'id'> & {
  /**
   * If disabled, the handle will not be interactive.
   */
  disabled?: boolean;
  /**
   * Called when the user clicks the handle.
   *
   * If `onClick` is passed (without `renderPopoverContent`), the handle will show a "+" icon on hover, which
   * acts like a button.
   *
   * (If `renderPopoverContent` is also passed, `onClick` is called when the user clicks the handle to open
   * or close the menu.)
   */
  onClick?: () => void;
  /**
   * Placement of the optional handle tooltip. If omitted, and `tooltipText` is provided, tooltip will be
   * displayed to the right of source handles, and to the left of target handles.
   */
  tooltipPlacement?: FloatingPlacement;
} & AtMostOneOf<{
    /**
     * Popover content, such as a `<Menu>`, to show when the handle is clicked.
     *
     * If `renderPopoverContent` is passed, the handle will show a "+" icon on hover, which acts as a popover
     * toggle.
     *
     * The popover is closed after the user clicks inside.
     *
     * To detect what the user has clicked, include callbacks within the content.
     *
     * Must not be provided if `tooltipText` is also provided.
     */
    renderPopoverContent?: () => JSX.Element;
    /**
     * Optional tooltip to display when the handle is hovered.
     *
     * When `tooltipText` is provided, the handle will show a tooltip on hover. Note that this means that when
     * disabled, the dot handle will now accept pointer events and the cursor will change to a pointer.
     *
     * Must not be provided if `renderPopoverContent` is also provided.
     */
    tooltipText?: string;
  }>;

export type ControlledAddNodeHandleProps = AddNodeHandleProps & {
  displayHandleAsHovered: boolean;
  setDisplayHandleAsHovered: (displayHandleAsHovered: boolean) => void;
};

/**
 * An interactive handle which displays an arrow icon on hover.
 *
 * This handle can live many lives:
 * • Show an arrow button on hover, if `onClick` is provided
 * • Show an arrow menu or popover toggle on hover, if `renderPopoverContent` is provided
 * • Act as draggable edge handle, if `isConnectable` is `true`
 * • Be a simple read-only indicator that a node could be connected, if `disabled` is `true`
 * • Show a tooltip on hover, if `tooltipText` is provided and
 *
 * Note that `tooltipText` and `renderPopoverContent` cannot both be provided.
 *
 * This component assumes the flowchart is arranged left-to-right.
 */
export function ControlledAddNodeHandle({
  disabled,
  onClick,
  renderPopoverContent,
  tooltipText,
  tooltipPlacement,
  displayHandleAsHovered,
  setDisplayHandleAsHovered,
  ...handleProps
}: ControlledAddNodeHandleProps): JSX.Element {
  // The `displayHandleAsHovered` prop is controlled by the parent component, and used in cases like the
  // EntityNode where the user wants hovering on the Node to display the arrow button. However, we need to
  // track the internal state of the handle being hovered, so we can show the tooltip only when the user
  // actually hovers over the handle.
  const {hovered: isHandleHoveredInternal, ref: handleHoverRef} = useHover<HTMLDivElement>();
  const [isAddIconPressed, setAddIconIsPressed] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  // When `onRequestClose` fires (outside click detected by floating-ui), it closes the popover.
  // If the outside click lands on the handle icon, the icon's onClick would re-toggle the popover
  // open. This ref prevents that by tracking whether onRequestClose just closed the popover, so the
  // icon's onClick can skip the toggle.
  const closedByDismissRef = useRef(false);

  // With `reactflow` v12 (`@xyflow/react`), we could use the narrower `useConnection` instead of `useStore`

  // `connectionHandleId` is the handle from which the connection was started
  const connectionHandleId = useStore((state) => state.connectionHandleId);

  // If the user is actively connecting edges, handles shouldn't activate our custom interactivity
  const isConnectingFromOtherHandle = connectionHandleId != null && connectionHandleId !== handleProps.id;

  const positionProps = {
    position: handleProps.type === 'target' ? Position.Left : Position.Right,
    style: {transform: calculateTranslation(handleProps.type)},
  };

  // If there are no custom interactions, the component is `disabled`, or a user is dragging an edge from
  // another handle, then we suppress the add icon.
  const suppressAddIcon = disabled || !(onClick || renderPopoverContent) || isConnectingFromOtherHandle;

  const handleDotStyles = [
    handleStyles.handleDot,
    handleProps.type === 'target' ? handleStyles.handleDotAlignedRight : handleStyles.handleDotAlignedLeft,
  ];

  // If the handle is explicitly disabled, or implicitly disabled if there are absolutely no custom
  // interactions (can't click, open a popover, nor connect an edge to this handle), then show as filled
  const implicitlyDisabled = !(onClick || renderPopoverContent || handleProps.isConnectable);
  const implicitlyOrExplicitlyDisabled = disabled || implicitlyDisabled;
  if (implicitlyOrExplicitlyDisabled) {
    handleDotStyles.push(handleStyles.handleDotFilled);

    // If the handle is disabled, but we wish to show tooltip, we must override Handle's disabled pointer
    // events. See `handleEventsOnChild` below for more details.
    if (tooltipText) {
      handleDotStyles.push(handleStyles.pointerCursor);
      handleDotStyles.push(handleStyles.pointerEventsEnabled);
    }
  } else {
    handleDotStyles.push(handleStyles.handleDotUnfilled);
  }

  /**
   * Depending on which interactivity is enabled, we sometimes add event handlers to `Handle`, and other times
   * to its child `div`.
   *
   * For `Handle` to be connectable (`isConnectable: true`), mouse events must originate on the `div` rendered
   * by `Handle`, and not any of its child elements. (`reactflow` adds several internal HTML classes to the
   * `div` rendered by `Handle`, and ignores crucial mouse events from elements which lack those classes.)
   *
   * In contrast, if `Handle` is not meant to be connectable (`isConnectable: false`), it ignores mouse events
   * for itself, by applying `pointer-events: none`. In this case, we cannot add our event handlers to
   * `Handle` itself, which would ignore them.
   *
   * If `isConnectable` is `true`, we:
   * • Set `pointer-events: none` on the child elements, so no events originate from them.
   * • Set `eventHandlerProps` on `Handle`.
   *
   * If `isConnectable` if `false`, we:
   * • Set `pointer-events: all` on the child `div`, so events are enabled for the child elements.
   * • Set `eventHandlerProps` on the child `div`, since `Handle` will ignore them.
   *
   * If we are suppressing the add icon, we always apply events to `Handle`.
   */

  const handleEventsOnChild = !suppressAddIcon && (isPopoverOpen || !handleProps.isConnectable);

  const eventHandlerProps = {
    onMouseEnter: (e: ElementMouseEvent) => {
      setDisplayHandleAsHovered(true);
      e.stopPropagation();
    },
    onMouseLeave: () => {
      setDisplayHandleAsHovered(false);
      // Don't show the icon as pressed, if the user has dragged their cursor away from the icon
      setAddIconIsPressed(false);
    },
    onMouseDown: (e: ElementMouseEvent) => {
      // Track if the icon is pressed, so we can show a different background color on the add icon
      setAddIconIsPressed(true);
      e.stopPropagation();
    },
    onClick: (e: ElementMouseEvent) => {
      // Since event handlers are applied for disabled handles which display tooltips, we have to watch out to
      // not trigger these when the handle is disabled.
      if (!disabled) {
        onClick?.();
        if (renderPopoverContent) {
          // If `onRequestClose` already closed the popover during this click's pointerdown phase,
          // don't re-toggle it open.
          if (closedByDismissRef.current) {
            closedByDismissRef.current = false;
          } else {
            setIsPopoverOpen((prev) => !prev);
          }
        }
      }
      setAddIconIsPressed(false);
      e.stopPropagation(); // Clicks within handles shouldn't be treated as clicks within nodes
    },
    onKeyDown: (e: ElementKeyboardEvent) => {
      if (isEscapeKey(e)) {
        setIsPopoverOpen(false);
      }
    },
  };

  return (
    <Handle
      ref={handleHoverRef}
      {...handleProps}
      {...positionProps}
      {...(handleEventsOnChild ? {} : {...eventHandlerProps})}
      isConnectable={disabled ? false : handleProps.isConnectable}
      // If not disabled, use `pointer: cursor`, since the add icon acts like a button
      className={css(
        handleStyles.handle,
        ...(implicitlyOrExplicitlyDisabled ? [] : [handleStyles.pointerCursor])
      )}
      {...getTestProps({
        component: 'AddNodeHandle',
        element: 'root',
      })}
    >
      {/* This is the filled or unfilled circle dot handle, which is shown when disabled, unhovered, or when
      an edge is being dragged from another handle. Note that it is actually just underneath the add icon if
      it is being hovered. */}
      <ControlledAddNodeHandleTooltip
        shouldWrapWithTooltip={suppressAddIcon}
        isOpen={isHandleHoveredInternal}
        tooltipText={tooltipText}
        placement={tooltipPlacement}
        type={handleProps.type}
      >
        <div className={css(handleDotStyles)} />
      </ControlledAddNodeHandleTooltip>

      {/* This is the add button and its wrapper. See `suppressAddIcon` for when it is not shown, otherwise,
      the add button is shown over the dot handle if it is being hovered over, or if a popover is currently
      open. */}
      {!suppressAddIcon && (
        <div
          {...(handleEventsOnChild ? {...eventHandlerProps} : {})}
          className={css(
            addIconStyles.addIconWrapper,
            // `reactflow` drag-and-drop connecting only works if events originate from the div rendered by
            // `Handle`; only enable pointer events on the child if drag-and-drop connecting is disabled, in
            // which case `Handle` applies `pointer-events: none` to itself, which must be overriden.
            handleEventsOnChild ? addIconStyles.pointerEventsEnabled : addIconStyles.pointerEventsDisabled
          )}
          {...getTestProps({
            component: 'AddNodeHandle',
            element: 'addIconWrapper',
          })}
        >
          {(displayHandleAsHovered || isPopoverOpen) && (
            <ControlledAddNodeHandleTooltip
              shouldWrapWithTooltip={!suppressAddIcon}
              isOpen={isHandleHoveredInternal}
              tooltipText={tooltipText}
              placement={tooltipPlacement}
              type={handleProps.type}
            >
              <ClickableIcon
                icon={`arrow-${handleProps.type === 'target' ? 'left' : 'right'}`}
                iconStyle={ClickableIconStyle.POSITIVE}
                className={css(
                  addIconStyles.addIcon,
                  isPopoverOpen && addIconStyles.isPopoverOpen,
                  // Show a darker background color while the mouse is down
                  isAddIconPressed && renderPopoverContent == null && addIconStyles.isAddIconPressed,
                  isAddIconPressed &&
                    renderPopoverContent != null &&
                    addIconStyles.isAddIconPressedBeforePopover
                )}
                dataTestAttrs={{
                  component: 'AddNodeHandle',
                  element: 'arrowButton',
                }}
              />
            </ControlledAddNodeHandleTooltip>
          )}
          {renderPopoverContent && (
            <div // Wrap `Float` in a `div` to catch events from within `Float`
              className={css(addIconStyles.popoverAnchor)} // Prevent this `div` from itself taking up space
              onMouseEnter={(e) => {
                // Prevent clicks from within the popover from being treated as clicks on the icon
                e.stopPropagation();
                // Ensure hovering over the popover registers as leaving the icon
                setDisplayHandleAsHovered(false);
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                // Close the popover when the user clicks within it.
                // In the future, we could make the close-on-click behavior configurable, for more complex
                // popover content.
                e.stopPropagation();
                setIsPopoverOpen(false);
                setDisplayHandleAsHovered(false);
                setAddIconIsPressed(false);
              }}
            >
              <Float
                interaction={FloatingInteraction.CONTROLLED}
                isOpen={isPopoverOpen}
                floatingElementOptions={{renderArrow: () => null}}
                placementOptions={{offsetOverride: 0}}
                onRequestClose={() => {
                  // Always close the popover when an outside click is detected.
                  // If the click lands on the handle icon, `closedByDismissRef` prevents the icon's
                  // onClick handler from re-toggling the popover open.
                  closedByDismissRef.current = true;
                  setIsPopoverOpen(false);
                  // Reset the flag after the full event chain (pointerdown → click) completes,
                  // so it doesn't affect future clicks.
                  setTimeout(() => {
                    closedByDismissRef.current = false;
                  }, 0);
                }}
                renderContent={renderPopoverContent}
              >
                <></>
              </Float>
            </div>
          )}
        </div>
      )}
    </Handle>
  );
}

export default function AddNodeHandle(props: AddNodeHandleProps): JSX.Element {
  const [displayHandleAsHovered, setDisplayHandleAsHovered] = useState<boolean>(false);
  return (
    <ControlledAddNodeHandle
      displayHandleAsHovered={displayHandleAsHovered}
      setDisplayHandleAsHovered={setDisplayHandleAsHovered}
      {...props}
    />
  );
}

const handleStyles = StyleSheet.create({
  handle: {
    // The actual handle is a large transparent circle, the same size as the add icon, to maintain a
    // consistent hover target. We cannot rely on `iconWrapper` being a child element of `handle` to widen the
    // hover target, since the child element sometimes has `pointer-events: none` applied.
    background: 'transparent',
    border: 'none', // Remove the default, white `reactflow` border
    borderRadius: TOTAL_ICON_WIDTH / 2,
    height: TOTAL_ICON_WIDTH,
    width: TOTAL_ICON_WIDTH,
  },
  handleDot: {
    // Shared styles for showing a small circle within the larger target of `handle`.
    borderRadius: HANDLE_DOT_WIDTH / 2,
    height: HANDLE_DOT_WIDTH,
    width: HANDLE_DOT_WIDTH,
    marginBottom: HANDLE_DOT_MARGIN,
    marginTop: HANDLE_DOT_MARGIN,
    // Child elements of `handle` cannot originate mouse events, for the handle to be connectable
    pointerEvents: 'none',
  },
  handleDotAlignedLeft: {
    // When <AddNodeHandle> is on the right, align the smaller `handleDot` to the left, so that the dot is
    // close to the node.
    marginLeft: 0,
    marginRight: 'auto',
  },
  handleDotAlignedRight: {
    // When <AddNodeHandle> is on the left, align the smaller `handleDot` to the right, so that the dot is
    // close to the node.
    marginLeft: 'auto',
    marginRight: 0,
  },
  handleDotUnfilled: {
    // Show a small, white circle with a light gray border within the larger target of `handle`. Visually,
    // this is the "handle" which is displayed whenenver the add icon isn't shown.
    background: Colors.WHITE,
    border: `solid 1px ${Color.GRAY_500}`,
  },
  handleDotFilled: {
    // Show a small, light gray circle within the larger target of `handle`. Visually, this is the "handle",
    // which is displayed whenenver the handle is disabled.
    background: Color.GRAY_400,
  },
  pointerCursor: {
    // Do not use the default `crosshair` when showing the add icon, which behaves like a button; apply to
    // `Handle` rather than child elements because `cursor` won't be respected on child elements which have
    // `pointer-events: none`.
    cursor: 'pointer',
  },
  pointerEventsEnabled: {
    pointerEvents: 'auto',
    userSelect: 'none', // Prevent drags and clicks from highlighting text in nodes
  },
});

const addIconStyles = StyleSheet.create({
  addIcon: {
    background: Colors.WHITE,
    border: `solid ${ICON_BORDER_WIDTH}px ${Colors.BLUE}`,
    borderRadius: TOTAL_ICON_WIDTH / 2,
    color: Colors.BLUE,
    height: ICON_WIDTH,
    padding: ICON_PADDING,
    transition: 'transform 300ms', // Animate the rotation to and from `isPopoverOpen`
    width: ICON_WIDTH,
  },
  addIconWrapper: {
    // Give the wrapper the same overall dimensions as the icon, so the hover target size remains consistent
    borderRadius: TOTAL_ICON_WIDTH / 2,
    height: TOTAL_ICON_WIDTH,
    width: TOTAL_ICON_WIDTH,
    // Position exactly over `handle`, which shares the same dimensions
    position: 'absolute',
    top: 0,
    left: 0,
  },
  isPopoverOpen: {
    background: Colors.BLUE_POWDER,
  },
  isAddIconPressed: {
    background: Colors.BLUE_POWDER,
  },
  isAddIconPressedBeforePopover: {
    background: Colors.BLUE_LIGHTEST, // Provide contrast before the steady background in `isPopoverOpen`
  },
  pointerEventsDisabled: {
    pointerEvents: 'none',
  },
  pointerEventsEnabled: {
    pointerEvents: 'auto',
    userSelect: 'none', // Prevent drags and clicks from highlighting text in nodes
  },
  popoverAnchor: {
    display: 'contents', // Keep this wrapper from itself taking up space
  },
});
